import type { EntitlementErrorResponse } from "../../types/licensing.types";
import { getCachedUser } from "../../auth/auth.service";
import { toast } from "./toast";
import { notifyLicensingChanged } from "../../auth/LicensingProvider";
import i18n from "../../i18n";

/**
 * A 402 from any service carries the meter, the figures and what the user
 * can do about it. Rendered as one persistent toast with the two actions
 * the licensing model offers: buy more, or upgrade.
 */
export function showEntitlementToast(error: EntitlementErrorResponse) {
  notifyLicensingChanged();
  const t = (key: string, defaultValue: string, opts: Record<string, unknown> = {}) =>
    i18n.t(`licensing:${key}`, { defaultValue, ...opts }) as string;

  const goto = (id: string) => {
    toast.dismiss(id);
    window.location.assign("/admin/subscription");
  };
  // Only an admin can act on the subscription page, and only an admin can
  // open it — anyone else was being sent to a "not authorised" screen.
  const cachedUser = getCachedUser();
  const canManage = !!(cachedUser?.isAdmin || cachedUser?.isAppAdmin);

  toast.custom(
    (item) => (
      <div className="w-[360px] max-w-[calc(100vw-2rem)] rounded-xl border border-red-500/30 bg-[rgb(var(--card))] shadow-xl overflow-hidden">
        <div className="px-4 py-3 bg-red-600 text-white">
          <div className="text-sm font-semibold">
            {error.code === "MODULE_NOT_LICENSED"
              ? t("toast.moduleTitle", "Not included in your plan")
              : error.code === "SUBSCRIPTION_INACTIVE"
                ? t("toast.inactiveTitle", "Subscription inactive")
                : t("toast.limitTitle", "{{meter}} limit reached", { meter: error.meterName || "Usage" })}
          </div>
        </div>
        <div className="px-4 py-3 text-sm text-[rgb(var(--text-primary))]">
          <p>{error.message}</p>
          {error.included !== null && error.included !== undefined && error.code === "ENTITLEMENT_EXCEEDED" && (
            <p className="mt-1 text-xs text-[rgb(var(--text-muted))]">
              {t("toast.figures", "Used {{used}} of {{included}} {{unit}}{{recharge}}", {
                used: error.used,
                included: error.included,
                unit: error.unit || "",
                recharge: error.rechargeAvailable > 0 ? ` (+${error.rechargeAvailable} recharged)` : "",
              })}
            </p>
          )}
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => toast.dismiss(item.id)}
              className="rounded-lg px-3 py-1.5 text-xs border border-[rgb(var(--border))] text-[rgb(var(--text-primary))] hover:bg-[rgb(var(--card-muted))]"
            >
              {t("toast.close", "Close")}
            </button>
            {!canManage && (
              <span className="text-xs text-[rgb(var(--text-muted))]">
                {t("toast.askAdmin", "Ask your organisation's admin to upgrade.")}
              </span>
            )}
            {canManage && error.suggestedActions?.includes("recharge") && (
              <button
                type="button"
                onClick={() => goto(item.id)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold border border-[rgb(var(--text-accent))] text-[rgb(var(--text-accent))] hover:bg-[rgb(var(--card-muted))]"
              >
                {t("toast.recharge", "Buy more")}
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={() => goto(item.id)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold bg-[rgb(var(--accent-fill))] text-[rgb(var(--accent-ink))] hover:opacity-90"
              >
                {t("toast.upgrade", "Upgrade plan")}
              </button>
            )}
          </div>
        </div>
      </div>
    ),
    { duration: 12000 },
  );
}
